import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  UserCheck,
  PackageOpen,
  Network,
  CarFront,
  Container,
  MapPinned,
  CheckCircle2,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { SERVICES, FAQ, ZONE, IMAGES } from "@/config/site";

const ICONS = { Truck, UserCheck, PackageOpen, Network, CarFront, Container };

const Services = () => {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <Seo
        title="Nos prestations de transport et logistique | ASTROLOG"
        description="Transport de marchandises, location avec conducteur, déménagement, commission de transport, transport de véhicules et convoi exceptionnel. France et Europe."
        path="/services"
        jsonLd={faqJsonLd}
      />

      {/* Hero */}
      <section className="brand-dark noise relative overflow-hidden py-20 sm:py-24">
        <div className="absolute inset-0">
          <img src={IMAGES.highway} alt="" aria-hidden="true" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-[#0A0E1A]/70" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h1 className="max-w-3xl font-heading text-4xl font-bold tracking-tighter text-[#FAFAF7] sm:text-5xl">
              Nos services de transport et de logistique
            </h1>
            <p className="mt-5 max-w-2xl text-lg text-slate-300">
              Six prestations complémentaires pour tous vos flux de marchandises,
              en France métropolitaine et en Europe.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Services détaillés */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8" data-testid="services-list">
        <div className="grid gap-8 lg:grid-cols-2">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon];
            return (
              <Reveal key={s.id} delay={(i % 2) * 100}>
                <article
                  id={s.id}
                  data-testid={`service-detail-${s.id}`}
                  className="flex h-full scroll-mt-24 flex-col rounded-3xl border border-slate-200 bg-white p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className="flex items-start gap-5">
                    <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary/10">
                      <Icon className="h-7 w-7 text-primary" strokeWidth={1.75} />
                    </span>
                    <div>
                      <h2 className="font-heading text-2xl font-semibold tracking-tight">{s.title}</h2>
                      <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.description}</p>
                    </div>
                  </div>
                  <ul className="mt-6 flex-1 space-y-2.5">
                    {s.prestations.map((p) => (
                      <li key={p} className="flex items-start gap-2.5 text-sm text-slate-700">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-secondary" />
                        {p}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/contact?service=${s.id}`}
                    data-testid={`service-devis-cta-${s.id}`}
                    className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#1D4ED8] sm:self-start"
                  >
                    Demander un devis pour ce service <ArrowRight className="h-4 w-4" />
                  </Link>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Zone d'intervention */}
      <section className="brand-dark noise relative py-16 sm:py-20" data-testid="zone-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <p className="flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-secondary">
              <MapPinned className="h-4 w-4" /> Zone d'intervention
            </p>
            <h2 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-[#FAFAF7] sm:text-4xl">
              La France et l'Europe, au quotidien
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {ZONE.map((z, i) => (
              <Reveal key={z.name} delay={(i % 4) * 60}>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-5 transition-colors duration-300 hover:border-secondary/40">
                  <h3 className="font-heading font-semibold text-[#FAFAF7]">{z.name}</h3>
                  <p className="mt-1 text-sm text-slate-400">{z.detail}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={420}>
              <div className="rounded-2xl border border-dashed border-white/20 p-5">
                <h3 className="font-heading font-semibold text-[#FAFAF7]">Plus loin ?</h3>
                <p className="mt-1 text-sm text-slate-400">Destinations plus lointaines sur demande.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-20" data-testid="faq-section">
        <Reveal className="text-center">
          <h2 className="font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
            Questions fréquentes
          </h2>
          <p className="mt-3 text-slate-600">Tout savoir avant de confier vos marchandises.</p>
        </Reveal>
        <Reveal delay={120}>
          <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
            {FAQ.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger data-testid={`faq-question-${i}`} className="text-left font-heading text-base font-semibold">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent data-testid={`faq-answer-${i}`} className="text-slate-600">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>
    </>
  );
};

export default Services;
